import { useConfirm } from '@/components/Confirm';
import {
  type AccountTag,
  type TagScope,
  createTag,
  deactivateTag,
  deleteTagPermanently,
  listTags,
  updateTag,
} from '@/lib/api';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const SCOPES: TagScope[] = ['customer', 'pet', 'booking'];

const SCOPE_LABELS: Record<TagScope, string> = {
  customer: 'Customer',
  pet: 'Pet',
  booking: 'Booking',
};

export function TagsSettingsPage() {
  const [scope, setScope] = useState<TagScope>('customer');
  const [tags, setTags] = useState<AccountTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#004AAD');
  const { confirm, ConfirmDialog } = useConfirm();

  async function refresh() {
    setLoading(true);
    try {
      const fresh = await listTags({ scope });
      setTags(fresh);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [scope]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createTag({ scope, name: newName.trim(), color: newColor });
      setNewName('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Tags</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tenant-defined labels for customers, pets, and bookings. Used by the task-board filter DSL
          and surfaced as chips on detail pages.
        </p>
      </div>

      <div className="mb-6 flex gap-1 rounded-full border border-border bg-surface p-1">
        {SCOPES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            className={
              s === scope
                ? 'rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground'
                : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
            }
          >
            {SCOPE_LABELS[s]}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleAdd} className="mb-6 rounded-xl border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium">Add {SCOPE_LABELS[scope].toLowerCase()} tag</h2>
        <div className="flex items-end gap-3">
          <label className="flex flex-col gap-1 text-xs flex-1">
            <span className="text-muted-foreground">Name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. VIP"
              className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Colour</span>
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="h-9 w-12 rounded-md border border-border bg-surface"
            />
          </label>
          <button
            type="submit"
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Add
          </button>
        </div>
      </form>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && tags.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">
            No {SCOPE_LABELS[scope].toLowerCase()} tags yet.
          </p>
        </div>
      )}

      {!loading && tags.length > 0 && (
        <ul className="space-y-1">
          {tags.map((tag) => (
            <li
              key={tag.id}
              className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
            >
              <div className="flex items-center gap-3">
                {tag.color && (
                  <span
                    aria-hidden
                    className="inline-block size-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                )}
                <span className="text-sm">{tag.name}</span>
                {tag.active === 0 && (
                  <span className="text-xs text-muted-foreground">(inactive)</span>
                )}
              </div>
              <div className="flex gap-2 text-xs">
                {tag.active === 1 && (
                  <button
                    type="button"
                    onClick={async () => {
                      const next = prompt('New name', tag.name);
                      if (next?.trim()) {
                        await updateTag(tag.id, { name: next.trim() });
                        void refresh();
                      }
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Edit
                  </button>
                )}
                {tag.active === 1 ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const yes = await confirm({
                        title: `Deactivate "${tag.name}"?`,
                        message:
                          'The tag will no longer appear in pickers. You can delete it permanently afterwards.',
                        confirmLabel: 'Deactivate',
                        danger: true,
                      });
                      if (!yes) return;
                      try {
                        await deactivateTag(tag.id);
                        void refresh();
                      } catch (err) {
                        setError((err as Error).message);
                      }
                    }}
                    className="text-destructive hover:underline"
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      const yes = await confirm({
                        title: `Delete "${tag.name}" permanently?`,
                        message:
                          'This will remove the tag and all its assignments. This cannot be undone.',
                        confirmLabel: 'Delete permanently',
                        danger: true,
                      });
                      if (!yes) return;
                      try {
                        await deleteTagPermanently(tag.id);
                        void refresh();
                      } catch (err) {
                        setError((err as Error).message);
                      }
                    }}
                    className="flex items-center gap-1 text-destructive hover:underline"
                  >
                    <Trash2 className="size-3" />
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
